/**
 * autoPreset3D.js — какой объёмный пресет поставить, если никто не выбирал.
 *
 * Зачем отдельным файлом. Правило маленькое, но ошибка в нём видна только на
 * реальном исследовании: неизвестный идентификатор apply3DPreset вернёт Err,
 * и окно останется чёрным — ровно так же, как если бы том не загрузился.
 * Отличить одно от другого на телефоне у постели больного нельзя.
 *
 * Здесь нет ни одного импорта — значит, правило можно прогнать тестом за
 * миллисекунду и сверить каждый выдаваемый идентификатор с presets3d.ts, не
 * поднимая ни cornerstone, ни DOM.
 *
 * Применяется во ВТОРОМ объёмном окне раскладки mpr6. Первое остаётся с
 * привычной костью (DEFAULT_3D_PRESET): врач знает, как выглядит «3D», и
 * менять это на существующих раскладках нельзя. Второе окно нужно ровно
 * затем, чтобы рядом стояло не то же самое.
 */

/**
 * Ключевые слова ищутся и в русском, и в английском: BodyPartExamined
 * заполняется латиницей по стандарту, а SeriesDescription на наших аппаратах
 * пишут по-русски — и то и другое приходится читать.
 *
 * Порядок правил — от узкого к широкому: «КТ-ангиография сонных артерий»
 * содержит и «ангио», и «сонн», но сосудистый пресет тут один и тот же;
 * а вот «КТ ОГК с контрастированием сосудов» должно попасть в сосуды, а не
 * в лёгкие, потому что искали именно сосуды.
 */
const RULES = [
  { preset: 'ct-vascular',        keys: ['angio', 'ангио', 'cta', 'vascular', 'сосуд', 'aort', 'аорт', 'carotid', 'сонн'] },
  { preset: 'cardiac-3d',         keys: ['coronar', 'корон', 'cardiac', 'heart', 'серд'] },
  { preset: 'ct-lung-parenchyma', keys: ['lung', 'chest', 'thorax', 'pulmon', 'лёгк', 'легк', 'груд', 'огк'] },
  { preset: 'custom-06-ct-dental', keys: ['dental', 'зуб', 'челюст', 'cbct', 'клкт'] },
  { preset: 'ct-colon',           keys: ['colon', 'колон', 'кишеч', 'толст'] },
];

/** Мягкие ткани вместе с костью — дополняет чистую кость первого окна. */
export const FALLBACK_PRESET = 'ct-overview';

/**
 * @param {string} modality — из generalSeriesModule (CT, MR, PT…)
 * @param {string} [hint] — BodyPartExamined и SeriesDescription одной строкой
 * @returns {string} идентификатор пресета из presets3d.ts
 */
export function autoPreset3D(modality, hint = '') {
  const m = String(modality || '').toUpperCase();
  const h = String(hint || '').toLowerCase();
  const has = (keys) => keys.some(k => h.includes(k));

  if (m === 'MR' || m === 'MRI') {
    // МР меряется не в HU: костные пресеты дают чёрный экран, а не «плохо».
    if (has(['angio', 'ангио', 'tof', 'mra'])) return 'custom-09-mri-angio';
    if (has(['t2', 'flair', 'ликвор', 'csf'])) return 'mri-wm-gm';
    return 'mri-brain';
  }

  for (const r of RULES) if (has(r.keys)) return r.preset;
  return FALLBACK_PRESET;
}

/** Все идентификаторы, которые функция способна вернуть — для проверки тестом. */
export const AUTO_PRESET_IDS = Object.freeze([
  ...RULES.map(r => r.preset),
  'custom-09-mri-angio', 'mri-wm-gm', 'mri-brain',
  FALLBACK_PRESET,
]);

export default { autoPreset3D, AUTO_PRESET_IDS, FALLBACK_PRESET };
